import dayjs from "dayjs";
import type { Shift } from "../types";

/** A shift whose times are known, so it can be loaded as a time window. */
export type TimedShift = Shift & { startDate: Date; endDate: Date };

const isTimed = (s: Shift): s is TimedShift => s.startDate !== undefined && s.endDate !== undefined;

/**
 * The shifts a person can step through on the Robots page, oldest first: timed, already started
 * (`startDate <= nowMs`), and still inside the page's lookback (`endDate >= cutoffMs`).
 */
export function pickableShifts(shifts: readonly Shift[], nowMs: number, cutoffMs: number): TimedShift[] {
	return shifts
		.filter(isTimed)
		.filter((s) => s.startDate.getTime() <= nowMs && s.endDate.getTime() >= cutoffMs)
		.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
}

/** The shift whose times are exactly `window`: what a shift pick loads. */
export function shiftMatchingWindow<S extends Shift>(
	shifts: readonly S[],
	window: { start: Date; end: Date },
): S | undefined {
	return shifts.find(
		(s) => s.startDate?.getTime() === window.start.getTime() && s.endDate?.getTime() === window.end.getTime(),
	);
}

/** The shift running at `nowMs` (started, not yet ended); the latest one if several overlap. */
export function runningShift<S extends Shift>(shifts: readonly S[], nowMs: number): S | undefined {
	let found: S | undefined;
	for (const s of shifts) {
		if (!s.startDate || !s.endDate) continue;
		if (s.startDate.getTime() > nowMs || s.endDate.getTime() < nowMs) continue;
		if (!found || s.startDate.getTime() > found.startDate!.getTime()) found = s;
	}
	return found;
}

const sameLocalDay = (a: Date, b: Date) =>
	a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Where `shift` falls among the shifts that started on its local day: the second of two is `{ index: 1, count: 2 }`. */
export function shiftDayPosition(shifts: readonly Shift[], shift: Shift): { index: number; count: number } {
	if (!shift.startDate) return { index: 0, count: 1 };
	const day = shifts
		.filter((s) => s.startDate && sameLocalDay(s.startDate, shift.startDate!))
		.sort((a, b) => a.startDate!.getTime() - b.startDate!.getTime());
	const index = day.findIndex((s) => s.id === shift.id);
	return index === -1 ? { index: 0, count: 1 } : { index, count: day.length };
}

/**
 * The next or previous shift from `current` within `pool` (oldest first), stopping at the ends. A `current` outside
 * the pool (another operator's shift while filtered, or a non-shift window) steps to the pool's newest shift.
 */
export function stepShift<S extends Shift>(
	pool: readonly S[],
	current: Shift | undefined,
	delta: number,
): S | undefined {
	if (pool.length === 0) return undefined;
	const at = current ? pool.findIndex((s) => s.id === current.id) : -1;
	if (at === -1) return pool[pool.length - 1];
	return pool[Math.max(0, Math.min(pool.length - 1, at + delta))];
}

/**
 * The words a shift answers to in a search: its operator's names and its start date written every way a person might
 * type it ("fri", "friday", "oct", "october", "23", "2026", "10/23", "10/23/2026", "2026-10-23"), plus "today" or
 * "yesterday" relative to `nowMs`. Lower-case.
 */
export function shiftSearchTerms(shift: Shift, nowMs: number): string[] {
	const terms = (shift.operator ?? "").toLowerCase().split(/\s+/).filter(Boolean);
	if (!shift.startDate) return terms;
	const d = dayjs(shift.startDate);
	terms.push(
		...d.format("ddd dddd MMM MMMM D YYYY").toLowerCase().split(" "),
		d.format("M/D"),
		d.format("MM/DD"),
		d.format("M/D/YYYY"),
		d.format("MM/DD/YYYY"),
		d.format("YYYY-MM-DD"),
	);
	const today = dayjs(nowMs).startOf("day");
	if (d.isSame(today, "day")) terms.push("today");
	if (d.isSame(today.subtract(1, "day"), "day")) terms.push("yesterday");
	return terms;
}

/**
 * Narrows shifts to one operator (by name; null keeps all) and to those matching `query`. Every word of the query must
 * start one of the shift's search terms (`shiftSearchTerms`), in any order: "fri", "friday", "octob", "oct 2",
 * "10/23", "jay fri" and "today" all match the way a person means them.
 */
export function filterShifts<S extends Shift>(
	shifts: readonly S[],
	filter: { operator: string | null; query: string; nowMs: number },
): S[] {
	const words = filter.query
		.toLowerCase()
		.split(/[\s,]+/)
		.filter(Boolean);
	return shifts.filter((s) => {
		if (filter.operator !== null && s.operator !== filter.operator) return false;
		if (words.length === 0) return true;
		const terms = shiftSearchTerms(s, filter.nowMs);
		return words.every((word) => terms.some((term) => term.startsWith(word)));
	});
}

export interface OperatorShifts<S extends Shift> {
	operator: string;
	operatorId?: string;
	/** Newest first. */
	shifts: S[];
}

/** Shifts grouped by operator name, each group newest first, the group with the most recent shift first. */
export function groupShiftsByOperatorNewestFirst<S extends Shift>(shifts: readonly S[]): OperatorShifts<S>[] {
	const groups = new Map<string, OperatorShifts<S>>();
	for (const s of shifts) {
		const operator = s.operator || "Unknown operator";
		const group = groups.get(operator) ?? { operator, operatorId: s.operatorId, shifts: [] };
		group.shifts.push(s);
		groups.set(operator, group);
	}
	const newest = (g: OperatorShifts<S>) => g.shifts[0]?.startDate?.getTime() ?? 0;
	for (const g of groups.values())
		g.shifts.sort((a, b) => (b.startDate?.getTime() ?? 0) - (a.startDate?.getTime() ?? 0));
	return [...groups.values()].sort((a, b) => newest(b) - newest(a));
}

/** Each shift's acres: the sum of its jobs' acres, by shift id. A job missing from `acresByJobId` adds nothing. */
export function acresByShift(
	shifts: readonly Shift[],
	acresByJobId: Readonly<Record<string, number>>,
): Map<string, number> {
	return new Map(shifts.map((s) => [s.id, (s.jobIds ?? []).reduce((sum, id) => sum + (acresByJobId[id] ?? 0), 0)]));
}
